import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

import type { LocationOrtho } from "context/CogOrtho";
import {
  formatOrthoFlightLabel,
  getOrthoFlightDate,
  getOrthoFlightTime,
} from "context/OrthoWorkflow";

interface AnalysisDateSliderProps {
  /** Orthos to pick between, oldest flight first. */
  orthos: readonly LocationOrtho[];
  onChange: (orthoId: number) => void;
  selectedOrthoId: number;
}

/** Narrowest a stop gets before the timeline scrolls sideways. */
const STOP_MIN_WIDTH = 56;

const sharesFlightDay = (
  ortho: LocationOrtho,
  orthos: readonly LocationOrtho[],
): boolean =>
  orthos.some(
    (other) =>
      other.id !== ortho.id &&
      getOrthoFlightDate(other) === getOrthoFlightDate(ortho),
  );

const formatFlightLabel = (
  ortho: LocationOrtho,
  orthos: readonly LocationOrtho[],
): string =>
  formatOrthoFlightLabel(
    getOrthoFlightDate(ortho),
    getOrthoFlightTime(ortho),
    sharesFlightDay(ortho, orthos),
  );

const formatFlightDay = (ortho: LocationOrtho): string => {
  const date = getOrthoFlightDate(ortho);
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format("MMM D") : date;
};

/** One stop per flight that has results, evenly spaced along a line. */
const AnalysisDateSlider: React.FC<AnalysisDateSliderProps> = ({
  onChange,
  orthos,
  selectedOrthoId,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stopRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [pendingOrthoId, setPendingOrthoId] = useState<number | null>(null);
  if (pendingOrthoId != null && pendingOrthoId === selectedOrthoId) {
    setPendingOrthoId(null);
  }
  const shownOrthoId = pendingOrthoId ?? selectedOrthoId;
  const selectedIndex = Math.max(
    orthos.findIndex((ortho) => ortho.id === shownOrthoId),
    0,
  );
  const selected = orthos[selectedIndex];

  // Keep the selected stop in view horizontally without scrolling the panel.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const stop = stopRefs.current[selectedIndex];
    if (scroller == null || stop == null) {
      return;
    }
    const scrollerRect = scroller.getBoundingClientRect();
    const stopRect = stop.getBoundingClientRect();
    if (stopRect.left < scrollerRect.left) {
      scroller.scrollBy({ left: stopRect.left - scrollerRect.left });
    } else if (stopRect.right > scrollerRect.right) {
      scroller.scrollBy({ left: stopRect.right - scrollerRect.right });
    }
  }, [selectedIndex]);

  if (selected == null) {
    return null;
  }

  const select = (index: number) => {
    const ortho = orthos[index];
    if (ortho == null) {
      return;
    }
    stopRefs.current[index]?.focus();
    if (ortho.id !== shownOrthoId) {
      setPendingOrthoId(ortho.id);
      onChange(ortho.id);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const nextIndex = {
      ArrowDown: selectedIndex + 1,
      ArrowLeft: selectedIndex - 1,
      ArrowRight: selectedIndex + 1,
      ArrowUp: selectedIndex - 1,
      End: orthos.length - 1,
      Home: 0,
    }[event.key];
    if (nextIndex == null) {
      return;
    }
    event.preventDefault();
    select(Math.min(Math.max(nextIndex, 0), orthos.length - 1));
  };

  return (
    <Stack spacing={0.5}>
      <Typography component="h3" variant="subtitle2">
        Flight date
      </Typography>
      <Typography aria-live="polite" variant="h6">
        {formatFlightLabel(selected, orthos)}
      </Typography>
      <Box ref={scrollerRef} sx={{ overflowX: "auto", pb: 0.5 }}>
        <Timeline
          aria-label="Flight date"
          onKeyDown={handleKeyDown}
          position="right"
          role="radiogroup"
          sx={{
            flexDirection: "row",
            m: 0,
            minWidth: orthos.length * STOP_MIN_WIDTH,
            p: 0,
          }}
        >
          {orthos.map((ortho, index) => {
            const isSelected = index === selectedIndex;
            return (
              <TimelineItem
                key={ortho.id}
                role="none"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  "&::before": { display: "none" },
                }}
              >
                <ButtonBase
                  aria-checked={isSelected}
                  aria-label={formatFlightLabel(ortho, orthos)}
                  onClick={() => select(index)}
                  ref={(element) => {
                    stopRefs.current[index] = element;
                  }}
                  role="radio"
                  sx={{
                    alignItems: "stretch",
                    flex: 1,
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    minWidth: 0,
                    py: 0.5,
                    "&.Mui-focusVisible": {
                      // Draw inside the stop. An outer outline is clipped by the
                      // scroller, which overflows on both axes.
                      outline: (theme) =>
                        `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: "-2px",
                    },
                  }}
                  tabIndex={isSelected ? 0 : -1}
                >
                  <TimelineSeparator
                    sx={{
                      flexDirection: "row",
                    }}
                  >
                    <TimelineConnector
                      sx={{
                        height: 2,
                        visibility: index === 0 ? "hidden" : "visible",
                      }}
                    />
                    <TimelineDot
                      color={isSelected ? "primary" : "grey"}
                      sx={{ m: 0 }}
                      variant={isSelected ? "filled" : "outlined"}
                    />
                    <TimelineConnector
                      sx={{
                        height: 2,
                        visibility:
                          index === orthos.length - 1 ? "hidden" : "visible",
                      }}
                    />
                  </TimelineSeparator>
                  <TimelineContent sx={{ flex: "none", p: 0 }}>
                    <Typography
                      color={isSelected ? "primary" : "text.secondary"}
                      noWrap
                      sx={{
                        display: "block",
                        fontWeight: isSelected ? "bold" : undefined,
                        lineHeight: 1.2,
                        textAlign: "center",
                      }}
                      variant="caption"
                    >
                      {formatFlightDay(ortho)}
                      {sharesFlightDay(ortho, orthos) && (
                        <>
                          <br />
                          {getOrthoFlightTime(ortho).slice(0, 5)}
                        </>
                      )}
                    </Typography>
                  </TimelineContent>
                </ButtonBase>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Box>
    </Stack>
  );
};

export default AnalysisDateSlider;
